import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { incrementRouterSpaceReactKey } from '~src/app/actions/spaces/IncrementRouterSpaceReactKeyAction';
import {
  ModifiedAtDetailSection,
  ModifiedByDetailSection
} from '~src/app/content/contentDetailDialog/CommonContentDetailSections';
import { ContentDetailSection, sectionContainer } from '~src/app/content/contentDetailDialog/ContentDetailSection';
import { ConnectedDeleteContentSection } from '~src/app/content/contentDetailDialog/DeleteContentSection';
import {
  DESCRIPTION_MAX_LENGTH,
  DescriptionDetailSection
} from '~src/app/content/contentDetailDialog/DescriptionDetailSection';
import { ConnectedOwnerDetailSection } from '~src/app/content/contentDetailDialog/OwnerDetailSection';
import { ProjectDetailSection } from '~src/app/content/contentDetailDialog/ProjectDetailSection';
import { TagsDetailSection } from '~src/app/content/contentDetailDialog/TagsDetailSection';
import { siteUrlsSelector } from '~src/app/selectors/SiteUrlsSelectors';
import { ServiceLocator } from '~src/app/ServiceLocator';
import { AppState } from '~src/app/state/AppState';
import { MapDispatchToProps, MapStateToProps } from '~src/app/utils/ConnectUtils';
import { Messages } from '~src/codegen/Localize';
import { ContentActionsService } from '~src/commonInfra/contentActions/ContentActions';
import { SizeFilter } from '~src/commonInfra/utils/SizeFilter';
import { ChangeTagsActionHelperService } from '~src/content/services/ChangeTagsActionHelper';
import { ChangeWorkbookDescriptionAction } from '~src/content/services/ChangeWorkbookDescriptionAction';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { ResultHelper } from '~src/httpClient/utils/ResultHelper';
import { RevisionHistoryActionInstance } from '~src/revisionHistory/services/RevisionHistoryAction';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { IWorkbookDetailInfoWithUsage } from '~src/ts/models/IWorkbookInfo';
import { Set } from '~src/ts/models/Set';
import { IWorkbookActions } from '~src/ts/ServerApi/types/IWorkbookActions';

import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { ConnectedTabbedViewsDetailSection } from './TabbedViewsDetailSection';


interface WorkbookDetailsDialogBodyOwnProps {
  readonly workbook: IWorkbookDetailInfoWithUsage;
  readonly allowedActions: IWorkbookActions;
}

interface WorkbookDetailsDialogBodyStateProps {
  readonly urls: SpacesSiteUrls;
}

interface WorkbookDetailsDialogBodyDispatchProps {
  readonly incrementRouterSpaceReactKey: () => void;
}

export type WorkbookDetailsDialogBodyProps =
    WorkbookDetailsDialogBodyOwnProps &
    WorkbookDetailsDialogBodyStateProps &
    WorkbookDetailsDialogBodyDispatchProps;

export function WorkbookDetailsDialogBody(props: WorkbookDetailsDialogBodyProps): JSX.Element {
  const { workbook, urls, incrementRouterSpaceReactKey } = props;

  const setDescription = React.useCallback((description: string) => {
    return ChangeWorkbookDescriptionAction.execute(workbook, description);
  }, [workbook]);

  const setTags = React.useCallback((tags: string[]) => {
    return ChangeTagsActionHelperService.changeTagsForContent([workbook], workbook.tags, tags)
        .then((results: IResult[]) => ResultHelper.combineResults(results));
  }, [workbook]);

  const moveToProject = React.useCallback(() => {
    return ContentActionsService.moveWorkbooksToProject(Set.singletonForIdItem(workbook));
  }, [workbook]);

  const showRevisionHistory = React.useCallback(() => {
    return RevisionHistoryActionInstance.showWorkbookRevisionHistory(workbook);
  }, [workbook]);

  function allowedAction(actionName: keyof IWorkbookActions): boolean {
    return !!(props.allowedActions?.[actionName]);
  }

  return (
    <div className={sectionContainer}>
      <DescriptionDetailSection
        description={workbook.description}
        isActionEnabled={allowedAction('setDescription')}
        setDescription={setDescription}
        descriptionMaxLength={DESCRIPTION_MAX_LENGTH}
        showPreviewAndFormattingHints={true}
        incrementRouterSpaceReactKey={incrementRouterSpaceReactKey}
      />
      <ProjectDetailSection
        project={workbook.project}
        isActionEnabled={allowedAction('setProject')}
        moveToProject={moveToProject}
        urls={urls}
        incrementRouterSpaceReactKey={incrementRouterSpaceReactKey}
      />
      <ConnectedOwnerDetailSection content={workbook} isActionEnabled={allowedAction('setOwner')} urls={urls}/>
      <TagsDetailSection
        tags={workbook.tags}
        isActionEnabled={allowedAction('setTags')}
        setTags={setTags}
        tagUrlFn={(tag: string) => (urls.workbooksWithTagUrl(tag))}
        incrementRouterSpaceReactKey={incrementRouterSpaceReactKey}
      />
      <ConnectedTabbedViewsDetailSection workbook={workbook} isActionEnabled={allowedAction('setDisplayTabs')} />
      <ContentDetailSection header={Messages.column_size()} detail={SizeFilter(workbook.size)} testId={'size'} />
      <ModifiedAtDetailSection
        date={workbook.updatedAt}
        allowShowRevisionHistory={FeatureFlags.detailsDialogsRevisionHistory && allowedAction('versionManagement')}
        showRevisionHistory={showRevisionHistory}
      />
      {FeatureFlags.modifiedByUserEnabled &&
        <ModifiedByDetailSection user={workbook.modifiedBy} urls={urls} />
      }
      {allowedAction('del') &&
        <ConnectedDeleteContentSection
          actionMessage={Messages.deleteWorkbook_action()}
          content={workbook}
        />
      }
    </div>
  );
}

const mapDispatchToProps: MapDispatchToProps<WorkbookDetailsDialogBodyDispatchProps> = (dispatch) => bindActionCreators({
  incrementRouterSpaceReactKey: incrementRouterSpaceReactKey
}, dispatch);

const mapStateToProps: MapStateToProps<WorkbookDetailsDialogBodyStateProps> = (state: AppState, ownProps: WorkbookDetailsDialogBodyOwnProps) => ({
  urls: siteUrlsSelector(state),
});

export const ConnectedWorkbookDetailsDialogBody: React.ComponentClass<WorkbookDetailsDialogBodyOwnProps> =
  connect(mapStateToProps, mapDispatchToProps)(WorkbookDetailsDialogBody);
